import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepButton from '@mui/material/StepButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Check from '@mui/icons-material/Check';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';

// ************** Types and Interfaces **************
export type StepperVariant = 'qonto';

export type StepIconStyle = 'dot' | 'numbered';

export type QontoStepperColors = {
    active?: string;
    completed?: string;
    inactive?: string;
};

export type StepAlertSeverity = 'error' | 'warning' | 'info';

export type StepAlert = {
    message: string;
    severity?: StepAlertSeverity;
};

export type StepperProps = {
    steps: string[];
    activeStep?: number;
    variant?: StepperVariant;
    stepIconStyle?: StepIconStyle;
    linear?: boolean;
    qontoColors?: QontoStepperColors;
    stepAlerts?: Record<number, StepAlert | string>;
    children?: React.ReactNode[];
    withControl?: boolean;
    onStepChange?: (step: number) => void;
};

type ResolvedQontoColors = Required<QontoStepperColors>;

const QontoColorsContext = React.createContext<ResolvedQontoColors | null>(null);

function resolveQontoColors(
    theme: Theme,
    qontoColors?: QontoStepperColors,
): ResolvedQontoColors {
    const inactiveDefault =
        theme.palette.mode === 'dark'
            ? theme.palette.grey[700]
            : theme.palette.grey[400];

    return {
        active: qontoColors?.active ?? theme.palette.primary.main,
        completed: qontoColors?.completed ?? theme.palette.primary.main,
        inactive: qontoColors?.inactive ?? inactiveDefault,
    };
}

function getAlertColor(theme: Theme, severity: StepAlertSeverity): string {
    switch (severity) {
        case 'error':
            return theme.palette.error.main;
        case 'warning':
            return theme.palette.warning.main;
        case 'info':
            return theme.palette.info.main;
        default:
            return theme.palette.info.main;
    }
}

// ******************* Custom Stepper Components *******************
const QontoConnector = styled(StepConnector, {
    shouldForwardProp: (prop) => prop !== 'ownerState',
})<{ ownerState: ResolvedQontoColors }>(({ ownerState }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: ownerState.active,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: ownerState.completed,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: ownerState.inactive,
        borderTopWidth: 3,
        borderRadius: 1,
    },
}));

const QontoStepIconRoot = styled('div')<{
    ownerState: { active?: boolean; colors: ResolvedQontoColors };
}>(({ ownerState }) => ({
    color: ownerState.active ? ownerState.colors.active : ownerState.colors.inactive,
    display: 'flex',
    height: 22,
    alignItems: 'center',
    '& .QontoStepIcon-completedIcon': {
        color: ownerState.colors.completed,
        zIndex: 1,
        fontSize: 18,
    },
    '& .QontoStepIcon-circle': {
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
}));

function ThemedQontoConnector() {
    const colors = React.useContext(QontoColorsContext);
    if (!colors) {
        return <StepConnector />;
    }
    return <QontoConnector ownerState={colors} />;
}

/**
 * Custom step icon for the Qonto variant of the stepper.
 */
function QontoStepIcon(props: StepIconProps) {
    const colors = React.useContext(QontoColorsContext);
    const { active, completed, className } = props;

    if (!colors) {
        return null;
    }

    return (
        <QontoStepIconRoot ownerState={{ active, colors }} className={className}>
            {completed ? (
                <Check className="QontoStepIcon-completedIcon" />
            ) : (
                <div className="QontoStepIcon-circle" />
            )}
        </QontoStepIconRoot>
    );
}

/**
 * MUI Stepper Component
 */
export default function HIStepper({
    steps,
    activeStep: controlledActiveStep = 0,
    variant = 'qonto',
    stepIconStyle = 'dot',
    linear = true,
    qontoColors,
    stepAlerts,
    children,
    onStepChange,
    withControl = false,
}: StepperProps) {
    const theme = useTheme();
    const hasContent = children && children.length > 0;
    const [internalStep, setInternalStep] = React.useState(controlledActiveStep);
    const [completed, setCompleted] = React.useState<Record<number, boolean>>({});
    const [resetCounters, setResetCounters] = React.useState<Record<number, number>>({});

    React.useEffect(() => {
        setInternalStep(controlledActiveStep);
    }, [controlledActiveStep]);

    const activeStep = hasContent ? internalStep : controlledActiveStep;
    const resolvedQontoColors = resolveQontoColors(theme, qontoColors);

    if (steps.length === 0) {
        return <Typography variant="body2">No steps provided.</Typography>;
    }

    const resetStepIfNotCompleted = (step: number) => {
        if (!completed[step]) {
            setResetCounters((prev) => ({
                ...prev,
                [step]: (prev[step] ?? 0) + 1,
            }));
        }
    };

    const handleStep = (step: number) => () => {
        resetStepIfNotCompleted(activeStep);
        setInternalStep(step);
        onStepChange?.(step);
    };

    const handleNext = () => {
        const next = Math.min(activeStep + 1, steps.length - 1);
        if (!linear) {
            resetStepIfNotCompleted(activeStep);
        }
        if (linear) {
            setCompleted((prev) => ({ ...prev, [activeStep]: true }));
        }
        setInternalStep(next);
        onStepChange?.(next);
    };

    const handleBack = () => {
        const prev = Math.max(activeStep - 1, 0);
        if (!linear) {
            resetStepIfNotCompleted(activeStep);
        }
        setInternalStep(prev);
        onStepChange?.(prev);
    };

    const handleCompleteStep = () => {
        setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    };

    const allStepsCompleted = () => {
        return Object.keys(completed).length === steps.length;
    };

    const useCustomIcon = stepIconStyle === 'dot';
    const StepIconComponent = useCustomIcon ? QontoStepIcon : undefined;
    const connector = useCustomIcon ? <ThemedQontoConnector /> : undefined;

    const renderStepLabel = (label: string, index: number) => {
        const rawAlert = stepAlerts?.[index];
        const alert: StepAlert | undefined =
            typeof rawAlert === 'string'
                ? { message: rawAlert, severity: 'warning' }
                : rawAlert;
        const severity = alert?.severity ?? 'warning';
        const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
        } = {};

        if (alert?.message) {
            labelProps.optional = (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 0.5,
                        textAlign: 'center',
                        color: getAlertColor(theme, severity),
                        lineHeight: 1.3,
                    }}
                >
                    {alert.message}
                </Typography>
            );
            labelProps.error = severity === 'error';
        }

        return (
            <StepLabel
                StepIconComponent={StepIconComponent}
                {...labelProps}
            >
                {label}
            </StepLabel>
        );
    };

    return (
        <QontoColorsContext.Provider value={resolvedQontoColors}>
            <Stack sx={{ width: '100%' }} spacing={2}>
                {variant === 'qonto' && (
                    <>
                        <Stepper
                            alternativeLabel
                            nonLinear={!linear}
                            activeStep={activeStep}
                            connector={connector}
                        >
                            {steps.map((label, index) => {
                                const isCompleted = linear
                                    ? index < activeStep || completed[index] === true
                                    : completed[index] === true;

                                return (
                                    <Step
                                        key={`qonto-${label}`}
                                        completed={isCompleted}
                                        disabled={linear && hasContent && index > activeStep && !completed[index]}
                                    >
                                        {!linear ? (
                                            <StepButton onClick={handleStep(index)}>
                                                {renderStepLabel(label, index)}
                                            </StepButton>
                                        ) : (
                                            renderStepLabel(label, index)
                                        )}
                                    </Step>
                                );
                            })}
                        </Stepper>

                        {hasContent && (
                            <Box sx={{ mt: 2 }}>
                                {children.map((child, index) => (
                                    <Box
                                        key={`step-content-${index}-${resetCounters[index] ?? 0}`}
                                        sx={{ display: index === activeStep ? 'block' : 'none', mb: 2 }}
                                    >
                                        {child}
                                    </Box>
                                ))}
                                {withControl && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Button
                                            disabled={activeStep === 0}
                                            onClick={handleBack}
                                        >
                                            Précédent
                                        </Button>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {!linear && (
                                                <Button
                                                    variant="outlined"
                                                    color={completed[activeStep] ? 'success' : 'primary'}
                                                    onClick={handleCompleteStep}
                                                >
                                                    {completed[activeStep] ? 'Étape complétée ✓' : 'Compléter l\'étape'}
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                disabled={activeStep === steps.length - 1 && (linear || allStepsCompleted())}
                                                onClick={handleNext}
                                            >
                                                Suivant
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Stack>
        </QontoColorsContext.Provider>
    );
}
